import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import ConfidenceMeter from '../components/ConfidenceMeter';
import NoteModal from '../components/NoteModal';
import '../css/ReviewPage.css';

// Toast/snackbar
function useToast() {
  const [toast, setToast] = useState(null);
  function showToast(msg, timeout = 1800) {
    setToast(msg);
    setTimeout(() => setToast(null), timeout);
  }
  return [toast, showToast];
}

function ReviewPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Accept questions prop (if passed from ResultsPage, otherwise fetch missed from API)
  const passedQuestions = location.state?.questions || null;

  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Per-question notes & confidence
  const [questionNotes, setQuestionNotes] = useState({});
  const [questionConfidence, setQuestionConfidence] = useState({});
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);

  // Rationale feedback
  const [feedbackGiven, setFeedbackGiven] = useState({});
  // Confidence filter (0 = all, 1/2/3 = filter)
  const [confidenceFilter, setConfidenceFilter] = useState(0);

  // Flag modal
  const [isFlagging, setIsFlagging] = useState(false);
  const [flagReason, setFlagReason] = useState('Typo');
  const [flagComment, setFlagComment] = useState('');

  const [toast, showToast] = useToast();

  // Load questions (from state or API)
  useEffect(() => {
    if (passedQuestions && Array.isArray(passedQuestions)) {
      setQuestions(passedQuestions);
      setIsLoading(false);
    } else {
      api.get('/quiz/questions/incorrect')
        .then(res => setQuestions(res.data?.questions || []))
        .catch(err => setError(err.response?.data?.message || err.message))
        .finally(() => setIsLoading(false));
    }
  }, [passedQuestions]);

  // Filtered indexes for confidence
const filteredIndexes = useMemo(() => (
  questions.length
    ? questions.map((q, i) => ({ i, conf: questionConfidence[i] || 0 }))
      .filter(({ conf }) => !confidenceFilter || conf === confidenceFilter)
      .map(({ i }) => i)
    : []
), [questions, questionConfidence, confidenceFilter]);

  // Adjust index when filter changes
  useEffect(() => {
    if (!filteredIndexes.length) setCurrentQuestionIndex(0);
    else if (!filteredIndexes.includes(currentQuestionIndex)) setCurrentQuestionIndex(filteredIndexes[0]);
    // eslint-disable-next-line
  }, [confidenceFilter, questions]);

  // Navigation
  const goToNext = useCallback(() => {
    const curIdx = filteredIndexes.indexOf(currentQuestionIndex);
    if (curIdx < filteredIndexes.length - 1)
      setCurrentQuestionIndex(filteredIndexes[curIdx + 1]);
  }, [filteredIndexes, currentQuestionIndex]);

  const goToPrevious = useCallback(() => {
    const curIdx = filteredIndexes.indexOf(currentQuestionIndex);
    if (curIdx > 0)
      setCurrentQuestionIndex(filteredIndexes[curIdx - 1]);
  }, [filteredIndexes, currentQuestionIndex]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.target.tagName === "TEXTAREA" || e.target.tagName === "INPUT") return;
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrevious]);

  // Notes
  const handleOpenNoteModal = (index) => {
    setEditingNoteIndex(index);
    setNoteModalOpen(true);
  };
  const handleSaveNote = (note) => {
    setQuestionNotes(prev => ({ ...prev, [editingNoteIndex]: note }));
    setNoteModalOpen(false);
    showToast('Note saved!');
    // Optionally persist to backend here
  };

  // Confidence ranking
  const handleSetConfidence = (level) => {
    setQuestionConfidence(prev => ({ ...prev, [currentQuestionIndex]: level }));
    showToast('Confidence updated!');
    // Optionally persist to backend here
  };

  // Rationale feedback
  const handleFeedback = (isHelpful) => {
    setFeedbackGiven(prev => ({ ...prev, [currentQuestionIndex]: isHelpful }));
    showToast(isHelpful ? 'Thanks for your feedback!' : 'Feedback noted.');
    // Optionally POST to backend
  };

  // Flag question for admin review
  const handleSubmitFlag = async (e) => {
    e.preventDefault();
    const questionId = questions[currentQuestionIndex].question_id;
    try {
      await api.post('/reports', {
        question_id: questionId,
        report_reason: flagReason,
        report_comment: flagComment
      });
      showToast('Report submitted. Thank you!');
    } catch {
      showToast('Failed to submit report.');
    } finally {
      setIsFlagging(false);
      setFlagComment('');
      setFlagReason('Typo');
    }
  };

  // Export/print/share: Only summary, not question text/answers
  const handleExport = () => {
    const summary = {
      reviewed: filteredIndexes.length,
      notes: Object.values(questionNotes).filter(Boolean).length,
      confidenceStats: [1,2,3].map(lvl => ({
        lvl,
        count: filteredIndexes.filter(i => questionConfidence[i] === lvl).length
      }))
    };
    const blob = new Blob([JSON.stringify(summary, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "review_summary.json";
    a.click();
    URL.revokeObjectURL(url);
  };
  const handlePrint = () => {
    // Create a secure print version with NO proprietary question content
    const safePrintData = {
      reviewSession: {
        totalQuestions: filteredIndexes.length,
        reviewedQuestions: filteredIndexes.map(idx => ({
          questionId: questions[idx].question_id,
          category: questions[idx].category_name,
          userAnswer: questions[idx].user_answer || 'N/A',
          correctAnswer: questions[idx].correct_choice,
          isCorrect: questions[idx].user_answer === questions[idx].correct_choice,
          confidence: questionConfidence[idx] || 0,
          hasNote: !!questionNotes[idx],
          // NO question_text, choices, or rationale included
        })),
        summary: {
          totalReviewed: filteredIndexes.length,
          notesCount: Object.values(questionNotes).filter(Boolean).length,
          confidenceStats: [1,2,3].map(lvl => ({
            level: lvl,
            count: filteredIndexes.filter(i => questionConfidence[i] === lvl).length
          }))
        },
        exportedAt: new Date().toISOString()
      }
    };

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>PA Quiz Review Session Summary</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .summary-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .summary-table th, .summary-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .summary-table th { background-color: #f2f2f2; }
            .confidence-low { color: #d32f2f; }
            .confidence-medium { color: #f57c00; }
            .confidence-high { color: #388e3c; }
            .no-proprietary { background: #fff3cd; border: 1px solid #ffeaa7; padding: 10px; margin: 20px 0; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>PA Quiz Review Session Summary</h1>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Questions Reviewed:</strong> ${safePrintData.reviewSession.totalQuestions}</p>
          </div>
          
          <div class="no-proprietary">
            <strong>Privacy Notice:</strong> This printout contains only question IDs, categories, and your performance data. 
            No proprietary question content, answer choices, or explanations are included to protect intellectual property.
          </div>

          <h2>Performance Summary</h2>
          <table class="summary-table">
            <thead>
              <tr>
                <th>Question ID</th>
                <th>Category</th>
                <th>Your Answer</th>
                <th>Correct Answer</th>
                <th>Result</th>
                <th>Confidence</th>
                <th>Has Note</th>
              </tr>
            </thead>
            <tbody>
              ${safePrintData.reviewSession.reviewedQuestions.map(q => `
                <tr>
                  <td>${q.questionId}</td>
                  <td>${q.category}</td>
                  <td>${q.userAnswer}</td>
                  <td>${q.correctAnswer}</td>
                  <td>${q.isCorrect ? '✓ Correct' : '✗ Incorrect'}</td>
                  <td class="confidence-${q.confidence === 3 ? 'high' : q.confidence === 2 ? 'medium' : 'low'}">
                    ${q.confidence === 0 ? 'Not Set' : q.confidence === 1 ? 'Low' : q.confidence === 2 ? 'Medium' : 'High'}
                  </td>
                  <td>${q.hasNote ? 'Yes' : 'No'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <h2>Session Statistics</h2>
          <table class="summary-table">
            <tr><th>Total Questions Reviewed</th><td>${safePrintData.reviewSession.summary.totalReviewed}</td></tr>
            <tr><th>Questions with Notes</th><td>${safePrintData.reviewSession.summary.notesCount}</td></tr>
            ${safePrintData.reviewSession.summary.confidenceStats.map(stat => 
              `<tr><th>Confidence Level ${stat.level}</th><td>${stat.count} questions</td></tr>`
            ).join('')}
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  // Retake filtered set (low confidence or missed only)
  const handleRetakeFiltered = () => {
    const filteredQuestions = filteredIndexes.map(i => questions[i]);
    navigate('/quiz', { state: { questions: filteredQuestions } });
  };

  if (isLoading) return <div className="review-container"><p>Loading review session...</p></div>;
  if (error) return <div className="review-container"><p>Error: {error}</p></div>;
  if (!filteredIndexes.length) {
    return (
      <div className="review-container">
        <h2>Review Session</h2>
        <p>No questions match your current filter. Great job!</p>
        <button onClick={() => setConfidenceFilter(0)}>Clear Filter</button>
        <Link to="/dashboard" className="dashboard-link">Return to Dashboard</Link>
      </div>
    );
  }

  const curIdx = filteredIndexes.indexOf(currentQuestionIndex) >= 0
    ? filteredIndexes.indexOf(currentQuestionIndex)
    : 0;
  const displayIdx = filteredIndexes[curIdx];
  const currentQuestion = questions[displayIdx];

  const choices = Object.entries(currentQuestion)
    .filter(([key, value]) => key.startsWith('choice_') && value)
    .map(([key, value]) => ({
      key: key.split('_')[1].toUpperCase(),
      value
    }));

  const mediaUrl = currentQuestion.media_url;
  const progress = ((curIdx + 1) / filteredIndexes.length) * 100;

  return (
    <div className="review-container">
      {toast && <div className="toast">{toast}</div>}

      {/* Note Modal */}
      <NoteModal
        isOpen={noteModalOpen}
        onClose={() => setNoteModalOpen(false)}
        note={editingNoteIndex != null ? questionNotes[editingNoteIndex] : ""}
        onSave={handleSaveNote}
      />

      {/* Flag Modal */}
      {isFlagging && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h3>Flag Question for Review</h3>
            <p><strong>Question:</strong> {currentQuestion.question_text}</p>
            <form onSubmit={handleSubmitFlag}>
              <div className="form-group">
                <label>Reason</label>
                <select value={flagReason} onChange={e => setFlagReason(e.target.value)}>
                  <option value="Typo">Typo</option>
                  <option value="Incorrect Answer">Correct Answer is Wrong</option>
                  <option value="Unclear Rationale">Rationale is Unclear</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Comment (Optional)</label>
                <textarea
                  value={flagComment}
                  onChange={e => setFlagComment(e.target.value)}
                  rows="4"
                  placeholder="Provide more detail..."
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setIsFlagging(false)}>Cancel</button>
                <button type="submit">Submit Report</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- Filter, export, share --- */}
      <div style={{ margin: "0.7em 0 1.1em 0", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <label style={{ fontWeight: 600 }}>Filter by Confidence:</label>
        <select value={confidenceFilter} onChange={e => setConfidenceFilter(Number(e.target.value))}>
          <option value={0}>All</option>
          <option value={1}>Low</option>
          <option value={2}>Medium</option>
          <option value={3}>High</option>
        </select>
        <button onClick={handleExport}>Export</button>
        <button onClick={handlePrint}>Print</button>
        <button onClick={() => {
          navigator.clipboard.writeText(window.location.href);
          showToast("Page link copied!");
        }}>Share</button>
        <button onClick={handleRetakeFiltered}>Retake These</button>
      </div>

      {/* Header/progress */}
      <div className="review-header" style={{ background: "#f3f6fa", padding: "0.7em 1.2em", borderRadius: 8 }}>
        <h2 style={{ margin: 0 }}>Review Session</h2>
        <span style={{ marginLeft: 15 }}>
          Question {curIdx + 1} of {filteredIndexes.length}
        </span>
        <button
          onClick={() => setIsFlagging(true)}
          className="flag-button"
          aria-label="Flag for Review"
          style={{
            marginLeft: 'auto',
            background: 'none',
            border: 'none',
            fontSize: '1.25em',
            cursor: 'pointer'
          }}
          title="Flag for Review"
        >
          ⚑ Flag
        </button>
      </div>

      {/* Progress bar */}
      <div className="review-progressbar">
        <div className="progress-inner" style={{ width: `${progress}%` }} />
        <span className="progress-text">{Math.round(progress)}% complete</span>
      </div>

      {/* Question card */}
      <div className="question-card">
        <p className="category-tag">{currentQuestion.category_name}</p>
        <h3 className="question-text">{currentQuestion.question_text}</h3>
        {mediaUrl && (
          <div className="question-media">
            {mediaUrl.match(/\.(jpg|jpeg|png|gif)$/i) && (
              <img src={mediaUrl} alt="Question" style={{ maxWidth: "100%", borderRadius: 8, marginBottom: 10 }} />
            )}
            {mediaUrl.match(/\.(mp3|wav|ogg)$/i) && (
              <audio controls src={mediaUrl} style={{ marginBottom: 10 }} />
            )}
            {mediaUrl.match(/\.(mp4|webm|mov)$/i) && (
              <video controls src={mediaUrl} style={{ maxWidth: "100%", borderRadius: 8, marginBottom: 10 }} />
            )}
          </div>
        )}

        {/* Choices - just show which was correct */}
        <div className="choices-container">
          {choices.map(({ key, value }) => (
            <button
              key={key}
              className={`choice-button ${key === currentQuestion.correct_choice ? 'correct' : 'disabled'}`}
              disabled
            >
              <span className="choice-key">{key}</span> {value}
            </button>
          ))}
        </div>

        {/* Per-question tools */}
        <div className="question-tools" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <span style={{ fontWeight: "bold" }}>Confidence:</span>
          <ConfidenceMeter
            value={questionConfidence[displayIdx] || 0}
            onChange={handleSetConfidence}
          />
          {questionNotes[displayIdx] && (
            <span className="note-indicator" title="Note saved!">
              <b>Note:</b> {questionNotes[displayIdx]}{" "}
              <button
                onClick={() => handleOpenNoteModal(displayIdx)}
                style={{ marginLeft: 5, fontSize: 15, background: "none", border: "none", cursor: "pointer", color: "#2577e6" }}
                title="Edit Note"
              >✎</button>
            </span>
          )}
          {!questionNotes[displayIdx] && (
            <button
              onClick={() => handleOpenNoteModal(displayIdx)}
              style={{ marginLeft: 10, fontSize: 15, background: "none", border: "none", cursor: "pointer", color: "#2577e6" }}
              title="Add Note"
            >Add Note 📝</button>
          )}
          {/* AI Explanation Stub */}
          <button
            disabled
            title="AI Explanation coming soon!"
            style={{ marginLeft: 12, background: "#ddd", border: "none", borderRadius: 5, color: "#666", cursor: "not-allowed", fontSize: 15, padding: "2px 7px" }}
          >
            Ask AI 🤖
          </button>
        </div>
        {/* Display note if saved */}
        {questionNotes[displayIdx] && (
          <p className="user-note-display">Note: {questionNotes[displayIdx]}</p>
        )}

        {/* Rationale + feedback */}
        <div className="rationale-box">
          <h4>Rationale</h4>
          <p>{currentQuestion.rationale}</p>
          <div className="feedback-buttons" style={{ marginTop: 6 }}>
            <button
              className={feedbackGiven[displayIdx] === true ? "feedback-yes selected" : "feedback-yes"}
              onClick={() => handleFeedback(true)}
              aria-label="Helpful"
              title="Helpful"
            >👍</button>
            <button
              className={feedbackGiven[displayIdx] === false ? "feedback-no selected" : "feedback-no"}
              onClick={() => handleFeedback(false)}
              aria-label="Not Helpful"
              title="Not Helpful"
            >👎</button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="review-navigation">
        <button onClick={goToPrevious} disabled={curIdx === 0}>&laquo; Previous</button>
        <Link to="/dashboard" className="dashboard-link">End Review</Link>
        <button onClick={goToNext} disabled={curIdx === filteredIndexes.length - 1}>Next &raquo;</button>
      </div>
    </div>
  );
}

export default ReviewPage;
